import type { ResourceMapVersion as MapVersion } from "@gettactic/api";
import { UserAvatar } from "@gettactic/components/src/data-display/UserAvatar/UserAvatar";
import { cn } from "@gettactic/helpers";
import {
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  RotateCcw,
  X
} from "lucide-react";
import React from "react";
import { IconButton } from "../../../ui/IconButton";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import { MapCanvasValue } from "../../contexts/MapCanvasContext/MapCanvasContext.types";

function isScheduledForFuture(version: MapVersion): boolean {
  if (!version.active_from || version.is_active) return false;
  return new Date(version.active_from) > new Date();
}

/**
 * Version history as a right-side drawer: a timeline of saved versions
 * with their state (active / scheduled / draft) and restore & activate
 * actions per version.
 */
export const MapVersionHistory: React.FC = () => {
  const { resourceId, loadMapValue, fetchMapVersions, activateMapVersion } =
    useMapCanvas();
  const [open, setOpen] = React.useState(false);
  const [versions, setVersions] = React.useState<MapVersion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [activatingId, setActivatingId] = React.useState<string | null>(null);

  const loadVersions = async () => {
    if (!resourceId || !fetchMapVersions) return;
    setLoading(true);
    try {
      const result = await fetchMapVersions(resourceId);
      setVersions(result);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!resourceId || !fetchMapVersions) return;
    setOpen(true);
    await loadVersions();
  };

  const handleRestore = (version: MapVersion) => {
    try {
      const mapValue = JSON.parse(version.map_data) as MapCanvasValue;
      loadMapValue(mapValue);
      setOpen(false);
    } catch {
      // invalid map data
    }
  };

  const handleActivate = async (version: MapVersion) => {
    if (!resourceId || !activateMapVersion) return;
    setActivatingId(version.id);
    try {
      await activateMapVersion(resourceId, version.id);
      await loadVersions();
    } finally {
      setActivatingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit"
    });

  const formatDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  // Group versions by calendar day so a busy day collapses to one row.
  const dayGroups = React.useMemo(() => {
    const groups: { day: string; items: MapVersion[] }[] = [];
    for (const version of versions) {
      const day = new Date(version.created).toDateString();
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.day === day) {
        lastGroup.items.push(version);
      } else {
        groups.push({ day, items: [version] });
      }
    }
    return groups;
  }, [versions]);

  // Older days start collapsed; the most recent day stays open.
  const [collapsedDays, setCollapsedDays] = React.useState<Set<string>>(
    new Set()
  );
  React.useEffect(() => {
    setCollapsedDays(new Set(dayGroups.slice(1).map((group) => group.day)));
  }, [dayGroups]);

  const toggleDay = (day: string) => {
    setCollapsedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  };

  if (!resourceId || !fetchMapVersions) return null;

  return (
    <>
      <IconButton label="Version history" isActive={open} onClick={handleOpen}>
        <History size={16} />
      </IconButton>

      {open && (
        <div className="absolute top-12 right-0 bottom-0 z-50 flex w-72 flex-col border-neutral-200 border-l bg-background-primary shadow-lg">
          <div className="flex h-10 flex-none items-center justify-between border-neutral-200 border-b py-2 pr-2 pl-4">
            <span className="font-semibold text-neutral-900 text-sm">
              Version history
            </span>
            <IconButton
              label="Close version history"
              size="sm"
              onClick={() => setOpen(false)}
            >
              <X size={15} />
            </IconButton>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {loading ? (
              <div className="px-4 py-8 text-center text-neutral-400 text-xs">
                Loading versions…
              </div>
            ) : versions.length === 0 ? (
              <div className="px-4 py-8 text-center text-neutral-400 text-xs">
                No saved versions yet
              </div>
            ) : (
              dayGroups.map((group) => {
                const isCollapsed = collapsedDays.has(group.day);
                const hasActive = group.items.some((item) => item.is_active);
                return (
                  <div key={group.day}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-1 px-3 py-2 text-left hover:bg-neutral-50"
                      onClick={() => toggleDay(group.day)}
                      aria-expanded={!isCollapsed}
                    >
                      {isCollapsed ? (
                        <ChevronRight size={13} className="text-neutral-400" />
                      ) : (
                        <ChevronDown size={13} className="text-neutral-400" />
                      )}
                      <span className="font-semibold text-neutral-500 text-xs uppercase tracking-wide">
                        {formatDayLabel(group.items[0].created)}
                      </span>
                      <span className="text-neutral-400 text-xs">
                        · {group.items.length}
                      </span>
                      {hasActive && isCollapsed && (
                        <span className="ml-auto flex-none rounded bg-blue-50 px-1 py-px font-semibold text-blue-700 text-xs uppercase tracking-wide">
                          Active
                        </span>
                      )}
                    </button>

                    {!isCollapsed &&
                      group.items.map((version) => {
                        const scheduled = isScheduledForFuture(version);
                        return (
                          <div
                            key={version.id}
                            className="group flex items-start gap-2 py-2 pr-3 pl-4 hover:bg-neutral-50"
                          >
                            <span
                              className={cn(
                                "mt-1 h-2 w-2 flex-none rounded-full border-2",
                                version.is_active
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-neutral-300 bg-background-primary"
                              )}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="truncate font-medium text-neutral-900 text-xs">
                                  {formatTime(version.created)}
                                </span>
                                {version.is_active && (
                                  <span className="flex-none rounded bg-blue-50 px-1 py-px font-semibold text-blue-700 text-xs uppercase tracking-wide">
                                    Active
                                  </span>
                                )}
                              </div>
                              {version.created_by_user && (
                                <div className="mt-1 flex min-w-0 items-center gap-1.5">
                                  <UserAvatar
                                    user={version.created_by_user}
                                    size="xs"
                                  />
                                  <span className="truncate text-neutral-600 text-xs">
                                    {version.created_by_user.name}
                                  </span>
                                </div>
                              )}
                              {scheduled && (
                                <span className="mt-1 flex items-center gap-1 font-medium text-amber-600 text-xs">
                                  <Calendar size={10} />
                                  Scheduled for{" "}
                                  {formatDate(version.active_from!)}
                                </span>
                              )}
                              {!version.is_active && !scheduled && (
                                <span className="text-neutral-400 text-xs">
                                  Draft
                                </span>
                              )}
                              {!version.is_active && (
                                <div className="mt-1 hidden items-center gap-2 group-hover:flex">
                                  {activateMapVersion && (
                                    <button
                                      type="button"
                                      className="flex items-center gap-1 rounded-md bg-green-50 px-2 py-1 font-medium text-green-700 text-xs hover:bg-green-100 disabled:opacity-50"
                                      onClick={() => handleActivate(version)}
                                      disabled={activatingId !== null}
                                    >
                                      <Check size={11} />
                                      {activatingId === version.id
                                        ? "Activating…"
                                        : "Make active"}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 font-medium text-blue-700 text-xs hover:bg-blue-100"
                                    onClick={() => handleRestore(version)}
                                  >
                                    <RotateCcw size={11} />
                                    Restore
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};
